import React, { useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Minus, Plus, ShieldCheck, Snowflake, FlaskConical,
  FileCheck2, Download, Mail, ChevronDown, Droplets, UserPlus,
} from "lucide-react";
import {
  getProduct, getVariant, hasMultipleVariants, stockLabel, CATEGORIES,
  showsFreeBacWater, PROMOS, resolveImage,
} from "../data/products";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../context/CatalogContext";
import ProductCard from "../components/ProductCard";
import ProductImage from "../components/ProductImage";
import RelatedCompounds from "../components/RelatedCompounds";
import { SalePrice, SaleBadge } from "../components/SalePrice";
import SaleCountdown from "../components/SaleCountdown";
import RestockNotifyForm from "../components/RestockNotifyForm";
import CoaRequestModal from "../components/CoaRequestModal";
import SEO from "../components/SEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const { products, loading } = useCatalog();
  const product = getProduct(products, slug);
  const { addItem, activeSeasonal } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [variantId, setVariantId] = useState(product?.defaultVariant);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [coaModalOpen, setCoaModalOpen] = useState(false);

  // Wait for catalog to load before redirecting away from a slug that may
  // exist (cold load with empty cache).
  if (!product && loading) {
    return <main className="container-x py-20 text-charcoal-400 text-sm">Loading…</main>;
  }
  if (!product) return <Navigate to="/products" replace />;

  const variant = getVariant(product, variantId);
  const multi = hasMultipleVariants(product);
  const stock = stockLabel(variant);
  const isOut = stock.tone === "out";
  const maxQty = Math.min(20, variant.stock || 0);
  const { src: variantImageSrc, fallback: variantImageFallback } = resolveImage(product, variant);

  const category = CATEGORIES.find((c) => c.id === product.category);
  const related = products.filter(
    (p) => p.slug !== product.slug && p.category === product.category
  ).slice(0, 3);

  // When user switches variant, clamp qty to that variant's stock.
  const handleVariantChange = (newId) => {
    setVariantId(newId);
    const newVariant = getVariant(product, newId);
    if (newVariant && qty > newVariant.stock) {
      setQty(Math.max(1, newVariant.stock));
    }
  };

  const handleAdd = () => {
    if (isOut) return;
    addItem(product.slug, variant.id, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleCOA = (e) => {
    if (!product.coaUrl) {
      e.preventDefault();
      setCoaModalOpen(true);
    }
  };

  const stockPillTone = {
    ok:  "bg-sage-100 text-sage-700",
    low: "bg-amber-50 text-amber-700",
    out: "bg-charcoal-100 text-charcoal-500",
  }[stock.tone];

  // Absolute URL helper for JSON-LD (search engines need canonical absolute image URLs)
  const toAbs = (u) => (u?.startsWith("http") ? u : `https://luviarx.com${u || ""}`);
  const productImages = [
    variantImageSrc,
    variantImageFallback,
    ...(product.variants?.map((v) => resolveImage(product, v).src).filter(Boolean) || []),
  ]
    .filter(Boolean)
    .map(toAbs)
    .filter((v, i, a) => a.indexOf(v) === i); // dedupe

  const offers = (product.variants || [variant]).map((v) => ({
    "@type": "Offer",
    url: `https://luviarx.com/product/${product.slug}`,
    priceCurrency: "USD",
    price: Number(v.price).toFixed(2),
    availability:
      (v.stock || 0) > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
    sku: `${product.slug}-${v.id}`,
    ...(v.label ? { name: v.label } : {}),
  }));

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description:
      product.tagline || product.description || `${product.name} — third-party tested research compound. For Research Use Only.`,
    image: productImages,
    sku: product.slug,
    ...(category ? { category: category.label } : {}),
    brand: { "@type": "Brand", name: "LuviaRx" },
    manufacturer: { "@type": "Organization", name: "LuviaRx" },
    offers:
      offers.length > 1
        ? {
            "@type": "AggregateOffer",
            offerCount: offers.length,
            priceCurrency: "USD",
            lowPrice: Math.min(...offers.map((o) => Number(o.price))).toFixed(2),
            highPrice: Math.max(...offers.map((o) => Number(o.price))).toFixed(2),
            availability: offers.some((o) => o.availability.endsWith("InStock"))
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            offers,
          }
        : offers[0],
  };

  return (
    <main data-testid="product-detail-page" className="py-12 md:py-16">
      <SEO
        title={`${product.name} · Research Compound`}
        description={`${product.name} — ${product.tagline || product.description?.slice(0, 150) || "Third-party tested research compound. For Research Use Only."}`}
        path={`/product/${product.slug}`}
        image={variantImageSrc}
        type="product"
        jsonLd={productJsonLd}
      />
      <div className="container-x">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-sm text-charcoal-400 hover:text-charcoal-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.8} /> Back to compounds
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Image */}
          <div className="relative">
            <div className="aspect-square rounded-[2rem] overflow-hidden bg-cream-200 shadow-soft">
              <ProductImage
                src={variantImageSrc}
                fallback={variantImageFallback}
                alt={`${product.name} · ${variant.label}`}
                className={`w-full h-full object-cover ${
                  product.slug === "cagrilintide" ? "scale-[1.15]" : ""
                } ${isOut ? "grayscale opacity-90" : ""}`}
              />
            </div>
            <span className="absolute top-5 left-5 badge-compliance bg-white/90 backdrop-blur">
              Research Use Only
            </span>
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {category && (
                <Link
                  to={`/products#${category.id}`}
                  className="overline hover:text-sage-700 transition-colors"
                >
                  {category.label}
                </Link>
              )}
              <span
                data-testid="product-detail-stock"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium ${stockPillTone}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${
                  stock.tone === "ok"  ? "bg-sage-500" :
                  stock.tone === "low" ? "bg-amber-500" :
                                         "bg-charcoal-300"
                }`} />
                {stock.label}
              </span>
            </div>

            <h1
              data-testid="product-detail-name"
              className="font-display text-4xl md:text-6xl font-bold leading-[0.95] text-charcoal-600 mb-3"
            >
              {product.name}
            </h1>
            <p className="text-charcoal-400 text-lg mb-6">{product.subtitle}</p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
              <SaleBadge slug={product.slug} />
              {activeSeasonal?.product_ids?.includes(product.slug) && (
                <SaleCountdown endDate={activeSeasonal.end_date} testId="pdp-sale-countdown" className="flex-wrap" />
              )}
            </div>
            <div className="flex items-baseline gap-4 mb-8">
              <SalePrice
                slug={product.slug}
                price={variant.price}
                testId="product-detail-price"
                className="font-display text-4xl font-bold text-charcoal-600"
              />
              <span className="text-charcoal-300 text-sm">USD · {variant.label}</span>
            </div>

            <p className="text-charcoal-500 leading-relaxed mb-7">{product.description}</p>

            {/* Variant selector — only shown when multiple variants exist */}
            {multi && (
              <div className="mb-7" data-testid="variant-selector">
                <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2">
                  Select size
                </label>
                <div className="relative">
                  <select
                    data-testid="variant-select"
                    value={variant.id}
                    onChange={(e) => handleVariantChange(e.target.value)}
                    className="appearance-none w-full md:w-72 bg-white border border-charcoal-100 rounded-full pl-5 pr-12 py-3 text-charcoal-600 font-medium cursor-pointer hover:border-sage-400 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-200 transition-all"
                  >
                    {product.variants.map((v) => (
                      <option
                        key={v.id}
                        value={v.id}
                        disabled={!v.inStock || v.stock <= 0}
                      >
                        {v.label} · ${v.price.toFixed(2)}
                        {(!v.inStock || v.stock <= 0) ? " (out of stock)" : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-400 pointer-events-none"
                    strokeWidth={2}
                  />
                </div>
              </div>
            )}

            {/* Specs */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              <Spec icon={FlaskConical} label="Purity" value={product.purity} />
              <Spec icon={Snowflake} label="Format" value="Lyophilized" />
              <Spec icon={FileCheck2} label="COA" value={product.coaUrl ? "Available" : "On request"} />
              <Spec icon={ShieldCheck} label="Testing" value="3rd-party" />
            </div>

            {/* Notes */}
            <ul className="space-y-2.5 text-sm text-charcoal-500 mb-8">
              {product.research_notes.map((n) => (
                <li key={n} className="flex gap-2.5 items-start">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage-500 shrink-0" />
                  {n}
                </li>
              ))}
            </ul>

            {/* COA action */}
            <div className="mb-7">
              {product.coaUrl ? (
                <a
                  href={product.coaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid="coa-download-link"
                  className="inline-flex items-center gap-2 text-sm font-medium text-sage-600 hover:text-sage-700 underline-offset-4 hover:underline"
                >
                  <Download className="w-4 h-4" strokeWidth={1.8} />
                  Download Certificate of Analysis (PDF)
                </a>
              ) : (
                <button
                  onClick={handleCOA}
                  data-testid="coa-request-button"
                  className="inline-flex items-center gap-2 text-sm font-medium text-sage-600 hover:text-sage-700 underline-offset-4 hover:underline"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.8} />
                  Request Certificate of Analysis
                </button>
              )}
              {coaModalOpen && (
                <CoaRequestModal
                  open={coaModalOpen}
                  onClose={() => setCoaModalOpen(false)}
                  product={product}
                  variant={variant}
                />
              )}
            </div>

            {/* Quantity & CTA */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className={`inline-flex items-center bg-cream-200 rounded-full p-1 self-start ${isOut ? "opacity-50" : ""}`}>
                <button
                  data-testid="qty-decrement"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={isOut}
                  className="w-9 h-9 rounded-full hover:bg-white transition-colors flex items-center justify-center disabled:cursor-not-allowed"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4" strokeWidth={2} />
                </button>
                <span data-testid="qty-value" className="w-10 text-center font-display font-bold text-charcoal-600">
                  {qty}
                </span>
                <button
                  data-testid="qty-increment"
                  onClick={() => setQty((q) => Math.min(maxQty || 1, q + 1))}
                  disabled={isOut || qty >= maxQty}
                  className="w-9 h-9 rounded-full hover:bg-white transition-colors flex items-center justify-center disabled:cursor-not-allowed"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <button
                data-testid={isAuthenticated ? "add-to-cart-button" : "create-account-to-purchase-button"}
                onClick={isAuthenticated ? handleAdd : () => navigate("/register", { state: { from: `/product/${product.slug}` } })}
                disabled={isAuthenticated && isOut}
                className="btn-primary flex-1 sm:flex-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isAuthenticated
                  ? (isOut ? "Out of stock" : added ? "Added to cart" : "Add to cart")
                  : (<><UserPlus className="w-4 h-4 mr-2" strokeWidth={2} />Create Account to Purchase</>)}
              </button>
            </div>

            {isOut && (
              <RestockNotifyForm
                productId={product.slug}
                variantId={variant.id}
                productName={product.name}
                variantLabel={variant.label}
              />
            )}

            {!isOut && variant.stock <= 5 && (
              <p data-testid="low-stock-note" className="mt-3 text-xs text-amber-700">
                Only {variant.stock} unit{variant.stock === 1 ? "" : "s"} left in current batch.
              </p>
            )}

            {showsFreeBacWater(product) && (
              <div
                data-testid="free-bacwater-note"
                className="mt-6 flex items-start gap-3 bg-sage-100/70 rounded-2xl px-4 py-3.5 border border-sage-200/60"
              >
                <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-sage-400 text-white shrink-0">
                  <Droplets className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
                <p className="text-sm text-charcoal-600 leading-relaxed">
                  <span className="font-medium">{PROMOS.freeBacWater.label}:</span> {PROMOS.freeBacWater.text}
                </p>
              </div>
            )}

            <p className="mt-8 text-xs text-charcoal-300 leading-relaxed">
              For Research Use Only. Not for Human Consumption. Products are intended exclusively for laboratory and scientific research purposes. Not FDA approved for diagnostic or therapeutic use.
            </p>
          </div>
        </div>

        {/* Related Compounds — horizontal carousel */}
      </div>
      <RelatedCompounds currentSlug={product.slug} productName={product.name} />
    </main>
  );
}

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 bg-cream-200/70 rounded-2xl p-4">
    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center">
      <Icon className="w-4 h-4 text-sage-600" strokeWidth={1.8} />
    </div>
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium">{label}</p>
      <p className="font-display font-bold text-charcoal-600 leading-tight">{value}</p>
    </div>
  </div>
);
